import Decimal from 'decimal.js';
import { StyledText } from '../../../atoms/Text/StyledText';
import { StyledSumElement } from '../../../atoms/UserPanelTrainerPay/StyledSumElement';
import { StyledSumRow } from '../../../atoms/UserPanelTrainerPay/StyledSumRow';
import { useMemo } from 'react';
import { taxPercent } from '../../../constants/pay';
import { getString } from '../../../../strings';

const CurrentMonthSummary = ({ hours }) => {
    const revenue = useMemo(
        () =>
            hours && Object.keys(hours).length && Object.values(hours)[0]
                ? Object.values(Object.values(hours)[0])
                      .filter((obj) => obj && obj.price)
                      .map((obj) => obj.price)
                      .reduce((prev, cur) => prev.plus(cur), new Decimal(0))
                : new Decimal(0),
        [hours]
    );

    const hoursAmount = useMemo(
        () =>
            hours && Object.keys(hours).length && Object.values(hours)[0]
                ? Object.values(Object.values(hours)[0])
                      .filter((obj) => obj && obj.hours)
                      .reduce((prev, cur) => prev.plus(cur.hours), new Decimal(0))
                : new Decimal(0),
        [hours]
    );

    const managerHoursAmount = useMemo(
        () =>
            hours && Object.keys(hours).length && Object.values(hours)[0]
                ? Object.values(Object.values(hours)[0])
                      .filter((obj) => obj && obj.managerHours != null)
                      .reduce((prev, cur) => prev.plus(cur.managerHours ?? 0), new Decimal(0))
                : new Decimal(0),
        [hours]
    );

    const hoursPlusManagerHoursAmount = useMemo(
        () =>
            hoursAmount && managerHoursAmount
                ? hoursAmount.plus(managerHoursAmount || new Decimal(0))
                : hoursAmount || managerHoursAmount || new Decimal(0),
        [hoursAmount, managerHoursAmount]
    );

    const paidHours = useMemo(
        () =>
            hours && Object.keys(hours).length && Object.values(hours)[0]
                ? Object.values(Object.values(hours)[0])
                      .filter((obj) => obj && obj.paidHours)
                      .reduce((prev, cur) => prev.plus(cur.paidHours), new Decimal(0))
                : new Decimal(0),
        [hours]
    );

    const halfHours = useMemo(
        () =>
            hours && Object.keys(hours).length && Object.values(hours)[0]
                ? Object.values(Object.values(hours)[0])
                      .filter((obj) => obj && obj.halfHours)
                      .reduce((prev, cur) => prev.plus(cur.halfHours), new Decimal(0))
                : new Decimal(0),
        [hours]
    );

    const free = useMemo(
        () =>
            hours && Object.keys(hours).length && Object.values(hours)[0]
                ? Object.values(Object.values(hours)[0])
                      .filter((obj) => obj && obj.free)
                      .map((obj) => obj.free)
                      .reduce((prev, cur) => prev.plus(cur), new Decimal(0))
                : new Decimal(0),
        [hours]
    );

    const trainerPayWithoutAdmin = useMemo(
        () =>
            hours && Object.keys(hours).length && Object.values(hours)[0]
                ? Object.values(Object.values(hours)[0])
                      .filter((obj) => obj && obj.pay)
                      .reduce((prev, cur) => prev.plus(cur.is_admin ? 0 : cur.pay), new Decimal(0))
                : new Decimal(0),
        [hours]
    );

    const remainingTrainerPay = useMemo(() => {
        if (!hours || !Object.keys(hours).length) {
            return new Decimal(0);
        }

        return Object.values(Object.values(hours)[0])
            .filter((obj) => obj && !obj.is_admin && obj.pay)
            .reduce((prev, cur) => {
                const totalDue = new Decimal(cur.pay);
                const totalPaid = cur.salary?.paidAmount
                    ? new Decimal(cur.salary.paidAmount)
                    : new Decimal(0);

                return prev.plus(totalDue.minus(totalPaid));
            }, new Decimal(0));
    }, [hours]);

    const income = useMemo(
        () =>
            revenue && trainerPayWithoutAdmin ? revenue.minus(trainerPayWithoutAdmin) : undefined,
        [revenue, trainerPayWithoutAdmin]
    );

    const revenueAfterTaxes = useMemo(
        () =>
            income
                ? income.times(new Decimal(1).minus(new Decimal(taxPercent).dividedBy(100)))
                : undefined,
        [income]
    );

    return (
        <StyledSumElement>
            <StyledSumRow iscentertext>
                <StyledText
                    hasdeclaredfontsize="30px"
                    hasdeclaredfontweight="600"
                    hasdeclaredlineheight="1.4em"
                    hasdeclaredfontcolor="#6786BE"
                    hasdeclaredpadding="0 0 20px 0"
                >
                    {getString('user_panel_all_trainer_pay_przychod')}:
                </StyledText>
                <StyledText
                    hasdeclaredfontsize="30px"
                    hasdeclaredfontweight="600"
                    hasdeclaredlineheight="1.4em"
                    hasdeclaredpadding="0 0 20px 0"
                    hasdeclaredtextalign="center"
                >
                    {revenue && (revenue.dp() > 0 ? revenue.toFixed(2) : revenue.toString())} PLN
                </StyledText>
            </StyledSumRow>
            <StyledSumRow iscentertext>
                <StyledText
                    hasdeclaredfontsize="18px"
                    hasdeclaredfontweight="600"
                    hasdeclaredlineheight="1.4em"
                    hasdeclaredfontcolor="#6786BE"
                >
                    {getString('user_panel_all_trainer_pay_godzin')}:
                </StyledText>
                <StyledText
                    hasdeclaredfontsize="18px"
                    hasdeclaredfontweight="600"
                    hasdeclaredlineheight="1.4em"
                    hasdeclaredtextalign="center"
                >
                    {paidHours.toString()} (D: {free.toString()} | P: {halfHours.toString()} | Ł:{' '}
                    {hoursAmount.toString()} | ŁD:{' '}
                    {hoursPlusManagerHoursAmount ? hoursPlusManagerHoursAmount.toString() : '0'})
                </StyledText>
            </StyledSumRow>
            <StyledSumRow iscentertext>
                <StyledText
                    hasdeclaredfontsize="18px"
                    hasdeclaredfontweight="600"
                    hasdeclaredlineheight="1.4em"
                    hasdeclaredfontcolor="#6786BE"
                >
                    {getString('user_panel_all_trainer_pay_wyplaty')}:
                </StyledText>
                <StyledText
                    hasdeclaredfontsize="18px"
                    hasdeclaredfontweight="600"
                    hasdeclaredlineheight="1.4em"
                    hasdeclaredtextalign="center"
                >
                    {trainerPayWithoutAdmin.dp() > 0
                        ? trainerPayWithoutAdmin.toFixed(2)
                        : trainerPayWithoutAdmin.toString()}{' '}
                    PLN{' '}
                    {remainingTrainerPay.gt(0) &&
                        !remainingTrainerPay.equals(trainerPayWithoutAdmin) &&
                        `(${
                            remainingTrainerPay.dp() > 0
                                ? remainingTrainerPay.toFixed(2)
                                : remainingTrainerPay.toString()
                        } PLN)`}
                </StyledText>
            </StyledSumRow>
            <StyledSumRow iscentertext>
                <StyledText
                    hasdeclaredfontsize="18px"
                    hasdeclaredfontweight="600"
                    hasdeclaredlineheight="1.4em"
                    hasdeclaredfontcolor="#6786BE"
                >
                    {getString('user_panel_all_trainer_pay_dochod')}:
                </StyledText>
                <StyledText
                    hasdeclaredfontsize="18px"
                    hasdeclaredfontweight="600"
                    hasdeclaredlineheight="1.4em"
                    hasdeclaredtextalign="center"
                >
                    {income.dp() > 0 ? income.toFixed(2) : income.toString()} (
                    {revenueAfterTaxes.dp() > 0
                        ? revenueAfterTaxes.toFixed(2)
                        : revenueAfterTaxes.toString()}
                    ) PLN
                </StyledText>
            </StyledSumRow>
        </StyledSumElement>
    );
};

export default CurrentMonthSummary;
